import { useFeatureFlags } from '@npco/mp-utils-mp-feature-flags'
import { useTranslations } from '@npco/utils-translations'
import {
  Box,
  BUTTON_SIZE,
  ButtonFill,
  ButtonGhost,
  COLOR,
  Flex,
  RadioGroupBasic,
  RadioListCard,
  showErrorToast,
  showSuccessToast,
  Typography,
} from '@npco/zeller-design-system'
import { Form, Formik, FormikHelpers } from 'formik'

import { GetCatalogSettingsUpdateQueryResponse } from 'hooks/useGetCatalogSettings/graphql/getCatalogSettings.generated'
import { GetItemSettingsQueryResponse } from 'hooks/useGetItemSettings/graphql/getItemSettings.generated'
import { SettingsLeavingRoutePromptGuard } from 'components/RouteLeavingPromptGuard'
import { SpinnerWrapped } from 'components/Spinner'
import { ToggleFormField, ToggleLabel } from 'components/ToggleFormField'
import { translationsShared } from 'translations'

import { GstDisplayInfobox } from './components/GstDisplayInfobox'
import { useGetItemCountForGstSettingLazyQuery } from './graphql/getItemCountForGstSetting.generated'
import { useItemsSettingsAnalytics } from './hooks/useItemsSettingsAnalytics'
import {
  ITEM_AUTO_GENERATE_SKU_FIELD,
  ITEM_TAX_APPLICABLE_FIELD,
  ITEM_TAX_INCLUSIVE_FIELD,
} from './ItemsSettingsForm.constants'
import { translations } from './ItemsSettingsForm.i18n'
import { ItemsSettingsFormFields } from './ItemsSettingsForm.types'

type ItemsSettingsFormProps = {
  settings?:
    | GetItemSettingsQueryResponse['getItemSettings']
    | GetCatalogSettingsUpdateQueryResponse['getCatalogSettings']
  isLoadingSettings: boolean
  updateSettings: (values: ItemsSettingsFormFields) => Promise<void>
  isUpdatingSettings: boolean
}

export const ItemsSettingsForm = ({
  isLoadingSettings,
  settings,
  updateSettings,
  isUpdatingSettings,
}: ItemsSettingsFormProps) => {
  const { trackSettingsUpdated } = useItemsSettingsAnalytics()
  const t = useTranslations(translations)
  const tShared = useTranslations(translationsShared)
  const [getItemCountForGstSetting, { data, loading: isLoading }] =
    useGetItemCountForGstSettingLazyQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        gstEnabled: true,
      },
    })
  const flags = useFeatureFlags()

  const itemCountForGstSetting = data?.getItemCountForGstSetting ?? 0
  const isLoadingItemCountForGstSetting =
    data?.getItemCountForGstSetting === undefined ? isLoading : false

  const handleFormSubmit = async (
    values: ItemsSettingsFormFields,
    { resetForm }: FormikHelpers<ItemsSettingsFormFields>
  ) => {
    try {
      await updateSettings(values)
      trackSettingsUpdated(values)

      resetForm({ values })
      showSuccessToast(t('successNotification'))
    } catch (e) {
      showErrorToast(t('errorNotification'))
    }
  }

  if (isLoadingSettings) {
    return <SpinnerWrapped variant="centre" />
  }

  const initialValues: ItemsSettingsFormFields = {
    isTaxApplicable: Boolean(settings?.itemsApplyTax),
    isTaxInclusive: Boolean(settings?.itemsTaxInclusive),
    shouldAutoGenerateSku:
      settings?.__typename === 'CatalogSettings'
        ? Boolean(settings?.autoSkuEnabled ?? true)
        : undefined,
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleFormSubmit}>
      {({ setFieldValue, values, handleSubmit, resetForm }) => {
        const shouldShowGstUpdateInfoBox =
          settings?.itemsTaxInclusive !== values.isTaxInclusive

        return (
          <Form onSubmit={handleSubmit}>
            <SettingsLeavingRoutePromptGuard />

            {isUpdatingSettings ? (
              <SpinnerWrapped variant="top" />
            ) : (
              <Flex gap="40px" flexDirection="column">
                <Typography component="h3" variant="heading-xl">
                  {t('heading')}
                </Typography>
                <Box>
                  <ToggleFormField
                    label={t('taxApplicableLabel')}
                    marginBottom="16px"
                    name={ITEM_TAX_APPLICABLE_FIELD}
                    value={false}
                  />
                  <Typography
                    component="p"
                    variant="body-base"
                    color={COLOR.GREY_550}
                  >
                    {t('taxApplicableDescription')}
                  </Typography>
                </Box>
                <Box>
                  <ToggleLabel>{t('taxPriceLabel')}</ToggleLabel>
                  <Typography
                    component="p"
                    variant="body-base"
                    color={COLOR.GREY_550}
                    marginTop="8px"
                  >
                    {t('taxPriceDescription')}
                  </Typography>
                  <Flex flexDirection="column" marginTop="24px">
                    <RadioGroupBasic
                      gap="8px"
                      isVerticalDisplay
                      name={ITEM_TAX_INCLUSIVE_FIELD}
                      onChange={(value) => {
                        setFieldValue(
                          ITEM_TAX_INCLUSIVE_FIELD,
                          value === 'true'
                        )
                        getItemCountForGstSetting()
                      }}
                      value={values.isTaxInclusive.toString()}
                      radioElementMarginBottom="8px"
                    >
                      <RadioListCard
                        id="gst-included"
                        title={t('taxInclusive')}
                        value="true"
                      />
                      <RadioListCard
                        id="gst-excluded"
                        title={t('taxExclusive')}
                        value="false"
                      />
                    </RadioGroupBasic>
                  </Flex>
                  {shouldShowGstUpdateInfoBox && (
                    <Box mt="24px">
                      <GstDisplayInfobox
                        isLoading={isLoadingItemCountForGstSetting}
                        itemCountForGstSetting={itemCountForGstSetting}
                      />
                    </Box>
                  )}
                </Box>

                {flags.POSItems && (
                  <>
                    <Typography component="h3" variant="heading-xl">
                      {t('itemManagement')}
                    </Typography>
                    <Box>
                      <ToggleFormField
                        label={t('autoGenerateSkuLabel')}
                        marginBottom="16px"
                        name={ITEM_AUTO_GENERATE_SKU_FIELD}
                        value={false}
                      />
                      <Typography
                        component="p"
                        variant="body-base"
                        color={COLOR.GREY_550}
                      >
                        {t('autoGenerateSkuDescription')}
                      </Typography>
                    </Box>
                  </>
                )}

                <Box
                  display="grid"
                  gap="24px"
                  gridTemplateColumns="156px 110px"
                >
                  <ButtonFill size={BUTTON_SIZE.MEDIUM} type="submit">
                    {tShared('save')}
                  </ButtonFill>
                  <ButtonGhost
                    size={BUTTON_SIZE.MEDIUM}
                    onClick={() => resetForm()}
                  >
                    {tShared('cancel')}
                  </ButtonGhost>
                </Box>
              </Flex>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
